import axios from 'axios';

// Default to same-origin /api so CRA dev proxy and the production Nginx proxy
// both work. Dokploy can still override this with REACT_APP_API_URL when the
// frontend should call the backend subdomain directly.
const API_BASE = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: (username, password) =>
    api.post('/auth/login', { username, password }),
  verify: () =>
    api.get('/auth/verify')
};

export const namecheapAPI = {
  getDomains: () =>
    api.get('/namecheap/domains'),
  getDNSRecords: (domain) =>
    api.get(`/namecheap/dns/${domain}`),
  updateDNSRecords: (domain, records) =>
    api.post(`/namecheap/dns/${domain}`, { records }),
  checkPropagation: (domain) =>
    api.get(`/namecheap/check/${domain}`)
};

export const healthAPI = {
  checkHealth: () =>
    api.get('/health'),
  checkVPSHealth: () =>
    api.get('/health/vps')
};

export const agentsAPI = {
  getState: () =>
    api.get('/agents/state'),
  getRunnerStatus: (config) =>
    api.get('/agents/runner/status', config),
  updateState: (state) =>
    api.put('/agents/state', state),
  createTask: (payload) =>
    api.post('/agents/task', payload),
  claimWork: (payload) =>
    api.post('/agents/runner/claim', payload),
  completeWork: (payload) =>
    api.post('/agents/runner/complete', payload)
};

// Build a query string from { period, start, end } — `start`/`end` win when
// both are provided (custom range), otherwise fall back to the preset period.
function buildRangeQuery(period, start, end) {
  const params = new URLSearchParams();
  if (start && end) {
    params.set('start', start);
    params.set('end', end);
  } else {
    params.set('period', period || '7d');
  }
  return params.toString();
}

export const analyticsAPI = {
  getProperties: () =>
    api.get('/analytics/properties'),
  getOverview: (period = '7d', start, end) =>
    api.get(`/analytics/overview?${buildRangeQuery(period, start, end)}`),
  getReport: (propertyId, period = '7d', start, end) => {
    const q = buildRangeQuery(period, start, end);
    return api.get(`/analytics/report?propertyId=${propertyId}&${q}`);
  },
  // Returns a URL suitable for <a href> downloads. The backend accepts the
  // JWT via ?token= because anchor clicks can't set custom headers. Kind is
  // one of: 'daily' | 'pages' | 'overview'.
  getExportUrl: (kind, period = '7d', start, end) => {
    const token = localStorage.getItem('token') || '';
    const q = buildRangeQuery(period, start, end);
    return `${API_BASE}/analytics/export.csv?kind=${encodeURIComponent(kind)}&${q}&token=${encodeURIComponent(token)}`;
  },
};

export default api;
